/** The status control for one preflight item — shared by the Preflight tab and
 *  the system-wide Items drawer, which previously each rendered their own bare
 *  `<Select>` over all four statuses.
 *
 *  Why it exists: setting "Completed" from a raw select writes the column and
 *  nothing else, bypassing the action's `on_manual_complete` — so the logo item's
 *  documented promise that it "refuses completion until a logo exists" was simply
 *  untrue on that path. Picking **Completed** here therefore routes through the
 *  `complete/` endpoint (the checked path, which records a run and can refuse);
 *  every other status still writes directly, because an ops override is a real
 *  need, but it now says what it skips first.
 *
 *  An offboarding item may be `skipped` or `superseded`, which no PATCH writes. A skipped
 *  item shows its status without offering it, and picking another one sets it as usual (an
 *  undo of the skip). A superseded item — the trace of a reopen, reset to pending by a
 *  re-close — is not set at all: the control is a plain chip (the backend would accept the
 *  write, and a completed item would then survive the re-close).
 */
import { Chip, MenuItem, Select, Tooltip } from '@mui/material';
import { useState } from 'react';

import type { PreflightStatus } from '@/api/preflight';
import { ConfirmDialog } from '@/components/ConfirmDialog';

import { PREFLIGHT_STATUSES, STATUS_LABELS, statusChipColor, statusColor } from './status';

interface Props {
  status: PreflightStatus;
  disabled?: boolean;
  /** POST `complete/` — validated completion (may be refused by the action). */
  onComplete: () => void;
  /** PATCH `status` — the raw override, taken only after the confirm. */
  onStatusChange: (status: PreflightStatus) => void;
}

export function PreflightStatusControl({ status, disabled, onComplete, onStatusChange }: Props) {
  const [override, setOverride] = useState<PreflightStatus | null>(null);

  const pick = (next: PreflightStatus) => {
    if (next === status) return;
    if (next === 'completed') {
      onComplete();
      return;
    }
    setOverride(next);
  };

  if (status === 'superseded') {
    return (
      <Tooltip
        describeChild
        title="Superseded when the tenant was reopened: nothing to set. Closing it again puts it back to pending."
      >
        <Chip
          size="small"
          tabIndex={0}
          label={STATUS_LABELS[status]}
          color={statusChipColor(status)}
          aria-label="Item status: Superseded"
        />
      </Tooltip>
    );
  }

  return (
    <>
      <Select
        variant="standard"
        value={status}
        disabled={disabled}
        aria-label="Item status"
        onChange={(e) => pick(e.target.value as PreflightStatus)}
        sx={{
          minWidth: 108,
          fontSize: 13,
          fontWeight: 600,
          color: statusColor(status),
          '& .MuiSelect-select': { color: statusColor(status) },
          '& .MuiSelect-icon': { color: statusColor(status) },
        }}
      >
        {/* An offboarding item's skipped is shown, never offered: no PATCH writes it
            (Skip… does, with a reason). */}
        {!PREFLIGHT_STATUSES.includes(status) && (
          <MenuItem
            value={status}
            disabled
            sx={{ fontSize: 13, fontWeight: 600, color: statusColor(status) }}
          >
            {STATUS_LABELS[status]}
          </MenuItem>
        )}
        {PREFLIGHT_STATUSES.map((s) => (
          <MenuItem key={s} value={s} sx={{ fontSize: 13, fontWeight: 600, color: statusColor(s) }}>
            {STATUS_LABELS[s]}
          </MenuItem>
        ))}
      </Select>

      <ConfirmDialog
        open={override != null}
        title={override ? `Set status to “${STATUS_LABELS[override]}” by hand?` : ''}
        message={
          'This writes the stored status directly: the action’s own checks are skipped and ' +
          'nothing is run or undone. Use Run or Mark done for the checked path.'
        }
        confirmLabel="Set status"
        onConfirm={() => {
          if (override) onStatusChange(override);
          setOverride(null);
        }}
        onCancel={() => setOverride(null)}
      />
    </>
  );
}
